import { createSelector, MemoizedSelector, Selector } from '@ngrx/store';

import { getMetaAdapter, getMetaSelectors } from '../meta-adapter';
import { Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { getSingleEntityActions } from './actions';
import { getSingleEntityDataAdapter } from './data';
import { NotNull, SingleEntityAdapter, SingleEntityState } from './types';

const singleEntityInitialState: SingleEntityState<any> = Object.freeze(<const>{
  data: null,
  loaded: false,
  pending: false,
  failed: false,
  error: null,
});

export const createSingleEntityAdapter = <Entity extends object | null>(
  baseSelector: Selector<any, SingleEntityState<Entity>>,
  namespace: string,
  references: Array<Reference<NotNull<Entity>>> = [],
) => {
  const adapter: SingleEntityAdapter<Entity> = {
    ...getMetaAdapter<SingleEntityState<Entity>>(),
    ...getSingleEntityDataAdapter(singleEntityInitialState),
  };

  const defaultActionsConfig = getSingleEntityActions<Entity>(adapter);
  const dataSelector = createSelector(baseSelector, state => state.data);
  const dataWithRefsSelector = <MemoizedSelector<any, Entity>>(
    createSelectorWithReferences(dataSelector, references, data => data)
  );

  const selectors = {
    ...getMetaSelectors(baseSelector, namespace),
    data: references.length ? dataWithRefsSelector : dataSelector,
    state: createSelector(baseSelector, (state: SingleEntityState<Entity>) => state),
  };

  return {
    adapter,
    selectors,
    defaultActionsConfig,
    initialState: <SingleEntityState<Entity>>singleEntityInitialState,
  };
};
